import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface StaffingCurveProps {
  data: { agents: number; sl: number; slA?: number }[];
  targetSL: number;
  showErlangA?: boolean;
}

export default function StaffingCurve({ data, targetSL, showErlangA }: StaffingCurveProps) {
  const datasets = [
    {
      label: 'Erlang C — Service Level',
      data: data.map((d) => d.sl * 100),
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.3,
      pointRadius: 2,
      borderWidth: 2,
    },
  ];

  if (showErlangA) {
    datasets.push({
      label: 'Erlang A — Service Level',
      data: data.map((d) => (d.slA ?? d.sl) * 100),
      borderColor: '#22d3ee',
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      fill: true,
      tension: 0.3,
      pointRadius: 2,
      borderWidth: 2,
    });
  }

  return (
    <div className="bg-navy-950/50 border border-gray-700/50 rounded-lg p-4">
      <Line
        data={{
          labels: data.map((d) => String(d.agents)),
          datasets,
        }}
        options={{
          responsive: true,
          interaction: { intersect: false, mode: 'index' },
          plugins: {
            legend: {
              display: showErlangA,
              labels: { color: '#9ca3af', font: { size: 11 } },
            },
            title: {
              display: true,
              text: 'Staffing Curve — Agents vs Service Level',
              color: '#e5e5e5',
              font: { size: 14, weight: 'bold' },
            },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed?.y?.toFixed(1) ?? ''}%`,
              },
            },
          },
          scales: {
            x: {
              title: { display: true, text: 'Agents', color: '#9ca3af' },
              ticks: { color: '#6b7280' },
              grid: { color: 'rgba(75, 85, 99, 0.2)' },
            },
            y: {
              title: { display: true, text: 'Service Level %', color: '#9ca3af' },
              ticks: { color: '#6b7280', callback: (v) => v + '%' },
              grid: { color: 'rgba(75, 85, 99, 0.2)' },
              min: 0,
              max: 100,
            },
          },
        }}
        plugins={[
          {
            id: 'targetLine',
            afterDraw(chart) {
              const yScale = chart.scales.y;
              const yPos = yScale.getPixelForValue(targetSL * 100);
              const ctx = chart.ctx;
              ctx.save();
              ctx.strokeStyle = '#ef4444';
              ctx.lineWidth = 1.5;
              ctx.setLineDash([6, 4]);
              ctx.beginPath();
              ctx.moveTo(chart.chartArea.left, yPos);
              ctx.lineTo(chart.chartArea.right, yPos);
              ctx.stroke();
              ctx.fillStyle = '#ef4444';
              ctx.font = '11px Inter';
              ctx.fillText(`Target: ${(targetSL * 100).toFixed(0)}%`, chart.chartArea.right - 80, yPos - 6);
              ctx.restore();
            },
          },
        ]}
      />
    </div>
  );
}
